import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type NotificationSeverity = 'success' | 'error' | 'warning' | 'info';

export interface NotificationOptions {
  message: string;
  severity?: NotificationSeverity;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  severity?: 'error' | 'warning' | 'info';
}

interface NotificationState extends NotificationOptions {
  open: boolean;
  key: number;
}

interface ConfirmState extends ConfirmOptions {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

interface NotificationContextType {
  notification: NotificationState;
  confirm: ConfirmState;
  showNotification: (options: NotificationOptions) => void;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
  hideNotification: () => void;
  showConfirm: (options: ConfirmOptions) => Promise<boolean>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notification, setNotification] = useState<NotificationState>({
    open: false,
    message: '',
    severity: 'info',
    duration: 6000,
    key: 0,
  });

  const [confirm, setConfirm] = useState<ConfirmState>({
    open: false,
    title: '',
    message: '',
    confirmText: 'Confirmer',
    cancelText: 'Annuler',
    severity: 'warning',
    onConfirm: () => {},
    onCancel: () => {},
  });

  const showNotification = useCallback((options: NotificationOptions) => {
    setNotification((prev) => ({
      open: true,
      message: options.message,
      severity: options.severity || 'info',
      duration: options.duration || 6000,
      action: options.action,
      key: prev.key + 1,
    }));
  }, []);

  const showSuccess = useCallback((message: string, duration?: number) => {
    showNotification({ message, severity: 'success', duration });
  }, [showNotification]);

  const showError = useCallback((message: string, duration?: number) => {
    showNotification({ message, severity: 'error', duration });
  }, [showNotification]);

  const showWarning = useCallback((message: string, duration?: number) => {
    showNotification({ message, severity: 'warning', duration });
  }, [showNotification]);

  const showInfo = useCallback((message: string, duration?: number) => {
    showNotification({ message, severity: 'info', duration });
  }, [showNotification]);

  const hideNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  const showConfirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirm({
        open: true,
        title: options.title,
        message: options.message,
        confirmText: options.confirmText || 'Confirmer',
        cancelText: options.cancelText || 'Annuler',
        severity: options.severity || 'warning',
        onConfirm: () => {
          setConfirm((prev) => ({ ...prev, open: false }));
          resolve(true);
        },
        onCancel: () => {
          setConfirm((prev) => ({ ...prev, open: false }));
          resolve(false);
        },
      });
    });
  }, []);

  const value: NotificationContextType = {
    notification,
    confirm,
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    hideNotification,
    showConfirm,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
